import { SonnerToaster, Toaster, TooltipProvider } from "@lumanity/ui/primitives";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PubCreatorLumanityProvider } from "@/lib/lumanity";
import { EvidenceViewerProvider } from "@/features/evidence";
import ProtectedRoute, { AdminRoute } from "@/components/ProtectedRoute";
import Login from "./pages/Login";
import Home from "./pages/Home";
import ProjectSetupPage from "./pages/ProjectSetupPage";
import ProjectWorkspace from "./pages/ProjectWorkspace";
import OutputPage from "./pages/OutputPage";
import NotFound from "./pages/NotFound";
import Admin from "./pages/admin/Admin";
import AllProjects from "./pages/admin/AllProjects";
import AiPermissionOptions from "./pages/admin/AiPermissionOptions";
import { OutlineTemplatesAdmin, TargetOutletsAdmin } from "@/features/outputs";
import { SectionPromptsAdmin } from "@/features/draft";
import {
  ProjectAvailability,
  ProjectCodes,
  ProjectProperties,
  RegisteredApps,
  SupportingDocumentTypes,
  TherapyAreas,
} from "./pages/admin/MasterDataPages";

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/projects" element={<Navigate to="/" replace />} />
      <Route path="/projects/new" element={<ProtectedRoute><ProjectSetupPage /></ProtectedRoute>} />
      <Route path="/projects/:id/edit" element={<ProtectedRoute><ProjectSetupPage /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectWorkspace /></ProtectedRoute>} />
      <Route path="/projects/:projectId/outputs/:outputId" element={<ProtectedRoute><OutputPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>}>
        <Route index element={<Navigate to="projects" replace />} />
        <Route path="projects" element={<AllProjects />} />
        <Route path="project-availability" element={<ProjectAvailability />} />
        <Route path="therapy-areas" element={<TherapyAreas />} />
        <Route path="project-codes" element={<ProjectCodes />} />
        <Route path="supporting-document-types" element={<SupportingDocumentTypes />} />
        <Route path="ai-permissions" element={<AiPermissionOptions />} />
        <Route path="project-properties" element={<ProjectProperties />} />
        <Route path="target-outlets" element={<TargetOutletsAdmin />} />
        <Route path="outline-templates" element={<OutlineTemplatesAdmin />} />
        <Route path="prompt-library" element={<SectionPromptsAdmin />} />
        <Route path="registered-apps" element={<RegisteredApps />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </>
  )
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PubCreatorLumanityProvider queryClient={queryClient}>
        <TooltipProvider>
          <EvidenceViewerProvider>
            <Toaster />
            <SonnerToaster />
            <RouterProvider router={router} />
          </EvidenceViewerProvider>
        </TooltipProvider>
      </PubCreatorLumanityProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
